import { Inline } from "PFComponents/containers";
import { DropdownOption } from "PFComponents/dropdown/dropdown";
import { Icon } from "PFComponents/icon";
import { allowInvites, getTargetType } from "PFCore/helpers/conversation";
import { Conversation } from "PFCore/types";
import { ReactElement } from "react";

import { getChatSendOnEnter } from "./send_on_enter";

export type BuildChatMenuOptionsParams = {
  t: (key: string) => string;
  conversation: Conversation;
  currentProfileId: number;
  isFollowing: boolean;
  targetUrl: string | false;
  showParticipants: () => void;
  handleFollowingChange: (follow: boolean) => void;
  handleInviteToChat: () => void;
  setSendOnEnter: (value: boolean) => void;
};

export const toOption = (id: string, el: ReactElement | string | null, fn: () => void): DropdownOption => ({
  id,
  displayElement: el || id,
  item: fn
});

// if the target is Profile you still have the links in participants list ("Chat Participants")
export const canShowOpenActivity = (conversation: Conversation, targetUrl: string | false) =>
  getTargetType(conversation) !== "Profile" && window.location.hash !== targetUrl;

export const getFollowOption = (
  isFollowing: boolean,
  t: (key: string) => string,
  handleFollowingChange: (follow: boolean) => void
) =>
  toOption(isFollowing ? t("unfollowThisChat") : t("followThisChat"), null, () =>
    handleFollowingChange(!isFollowing)
  );

export const getSendOnEnterOption = (
  sendsOnEnter: boolean,
  t: (key: string) => string,
  setSendOnEnter: (value: boolean) => void
) =>
  toOption(
    sendsOnEnter ? "_show" : "_hide",
    <Inline alignItems="center">
      {sendsOnEnter && <Icon name="check" size="sm" />}
      {t("pressEnterToSend")}
    </Inline>,
    () => setSendOnEnter(!sendsOnEnter)
  );

export const buildChatMenuOptions = ({
  t,
  conversation,
  currentProfileId,
  isFollowing,
  targetUrl,
  showParticipants,
  handleFollowingChange,
  handleInviteToChat,
  setSendOnEnter
}: BuildChatMenuOptionsParams): DropdownOption[] => {
  const sendsOnEnter = getChatSendOnEnter();
  const allowsInvites = allowInvites(conversation, currentProfileId);
  const showOpenActivity = canShowOpenActivity(conversation, targetUrl);

  return [
    toOption(t("chatParticipants"), null, showParticipants),
    allowsInvites && toOption(t("inviteToChat"), null, handleInviteToChat),
    showOpenActivity && toOption(t("openActivity"), null, () => window.location.assign(targetUrl as string)),
    getFollowOption(isFollowing, t, handleFollowingChange),
    getSendOnEnterOption(sendsOnEnter, t, setSendOnEnter)
  ].filter(Boolean) as DropdownOption[];
};
